import React, {useEffect, useState, useTransition} from 'react';
import {Button, ButtonDropdown, Checkbox, CircleLoading, Flexbox, HorDiv, Input, Label, ScrollView, Section, Select} from "../index";
import {
    checkNull,
    checkNullArr,
    checkNullJson,
    checkNullStr,
    dropJsonListDuplicates,
    getArrLen,
    getJsonValue,
    getDefStr,
    isArr,
    isBoolTrue,
    isJsonValueTrue,
    jsonToStr,
    limitStringWords,
    parseInteger,
    printError,
    printLog
} from "../utils/ReactUtils";
import {THEME} from "../config/FusionConfig";

/**
 * A searchable check-list component.
 *
 * @param {string} wrapperClassName -
 *
 * @example
 * // Usage example:
 * const Fields = {
 *     currSelectId='currSelectId',
 *     currSelectName='currSelectName',
 *     defSelectItem='defSelectItem',
 *     selectOptions='selectOptions',
 *     showSelectListLoader='showSelectListLoader',
 *     checkList='checkList',
 *     checkList_SelectedOnly='checkList_SelectedOnly',
 *     showCheckListLoader='showCheckListLoader',
 * };
 * const [fieldsData, setFieldsData] = useState({});
 *
 * //checkList data-sample example:
 * const checkList=[ (to display at the end as tag)
 *  { id: 1, name: 'Name 1' }, //basic check-item
 *  { id: 1, name: 'Name 1', original: true, selected: true }, //check-item with default selected & original to show it with outline
 *  { id: 1, name: 'Name 1', original: true, selected: true, variant: 'primary', tooltip: 'Tooltip', disabled: false, hide: false }, //check-item with variant (outline-color, def-selection color), subtitle (to display next to 'name' value)
 *  { id: 1, name: 'Name 1', original: true, selected: true, variant: 'primary', tooltip: 'Tooltip', disabled: false, hide: false }, //check-item with tag (to display  at the end as tag)
 *  { id: 1, name: 'Name 1', original: true, selected: true, variant: 'primary', tooltip: 'Tooltip', disabled: false, hide: false, subtitle: "Subtitle", endSubtitle:"End-Subtitle", tag: "Tag", subtitleTooltip: "Subtitle Tooltip", endSubtitleTooltip: "End Subtitle Tooltip", tagTooltip: "Tag Tooltip", iMsg: "I-msg" },  //heck-item with all options to customize each check-item
 * ];
 *
 * <SearchableCheckboxList
 *                 wrapperClassName={''}
 *                 borderBoxClassName={''}
 *                 borderless={true}
 *                 variant={THEME}
 *                highlightMatchedItem={true}
 *                 //generic
 *                 label={''}
 *
 *                 //select
 *                 showSelect={true}
 *                 selectLabel={'Choose an item'}
 *                 selectDefOption={getField(Fields.defSelectItem)}
 *                 selectOptions={getField(Fields.selectOptions, [])}
 *                 selectIdKey={'id'}
 *                 selectTextKey={'name'}
 *                 disableSelect={isFieldTrue(Fields.showCheckListLoader)}
 *                 showSelectLoader={isFieldTrue(Fields.showCheckListLoader)}
 *                 onSelectChange={(e, value, text) => {
 *                     log('onSelectChange.value:', value, 'text:', text);
 *
 *                     updateField(Fields.currSelectName, text);
 *                     updateField(Fields.currSelectId, value);
 *                 }}
 *
 *                 //cb-list
 *                 showDataList={true}
 *                 showSelectAll={true}
 *                 showDisplaySelectedOnly={true}
 *                 showSelectedCount={true}
 *                 checkBinarySelection={false}
 *                 isCheckItemJson={true}
 *                 checkItemJsonIdKey={'id'}
 *                 checkItemJsonTextKey={'name'}
 *                 checkDataList={getField(Fields.checkList, [])}
 *                 showDataListLoader={isFieldTrue(Fields.showCheckListLoader)}
 *                 onItemCheck={(e, value, updatedList, newCheckedList) => {
 *                     log('onItemCheck.value:', value, 'updatedList:', updatedList, 'len:', updatedList.length);
 *                     log('onItemCheck.value:', value, 'newCheckedList:', newCheckedList, 'len:', newCheckedList.length);
 *                     updateField(Fields.checkList_SelectedOnly, updatedList);
 *                 }}
 *                 onValueChange={(e, value, selectedList, newCheckedList, updatedList) => {
 *                     log('onItemCheck.value:', value, 'selectedList:', selectedList, 'len:', selectedList.length);
 *                     log('onItemCheck.value:', value, 'updatedList:', updatedList, 'len:', updatedList.length);
 *                     log('onItemCheck.value:', value, 'newCheckedList:', newCheckedList, 'len:', newCheckedList.length);
 *                     updateField(Fields.checkList_SelectedOnly, updatedList);
 *                 }}
 *
 *                 //search
 *                 searchable={true}
 *                 onSearch={(value) => {
 *                     log('onSearch.value:', value,);
 *                 }}
 *
 *                 //sort
 *                 showSort={true}
 *                 sortByParameters={['name', 'subtitle', 'endSubtitle', 'tag']}
 *  />
 *
 *      function updateField(field, value) {
 *         setFieldsData(prevState => ({...prevState, [field]: value}));
 *     }
 *
 *     function getField(field, defValue) {
 *         return getJsonValue(fieldsData, field, defValue);
 *     }
 *
 *     function isFieldTrue(field) {
 *         return isBoolTrue(getField(field));
 *     }
 */
const SearchableCheckboxList = React.memo(({
                                               wrapperClassName = "",
                                               borderBoxClassName = "",
                                               searchClassName = "",
                                               cbClassName = "",
                                               cbWrapperClassName = "",
                                               selectClassName = "",
                                               selectLabelClassName = "",
                                               scrollerClassName = "",
                                               scrollerId = "",
                                               scrollWrapperClassName = "",
                                               listWrapperClassName = "",
                                               labelClassName = "",
                                               headerClassName = "",
                                               cbCategoryWrapperClassName = "",
                                               cbCategoryLabelClassName = "",
                                               countLabelClassName = "",
                                               sortClassName = "",
                                               msgClassName = "",
                                               cbCategoryLabelAt = "start",
                                               roundedVariant = 3,
                                               searchPlaceholder = "Search...",

                                               padded = true,
                                               pad = '3',
                                               selectBelowPad = '3',
                                               borderless = true,
                                               borderlessList = false,
                                               selectable = true,
                                               selectType = 'checkbox',//radio, highlight, bordered, highlightBorder

                                               borderColor = "gray",
                                               scrollVariant = "dark",
                                               listHeight = "35vh",
                                               variant = THEME,
                                               noSearchFoundMsg = 'Search not found!',
                                               noDataFoundMsg = 'No data found!',
                                               highlightMatchedColorVariant = 'warning',

                                               required = false,
                                               showHeader = true,
                                               inlineHeader = false,
                                               preventRerender = false,
                                               label,

                                               checkDataList = [],//{name, id, selected:true, original:true}
                                               isCheckItemJson = false,
                                               originalCheckItemOutlined = false,
                                               minifySelectedList = false,
                                               highlightMatchedItem = true,
                                               checkItemJsonIdKey,
                                               checkItemJsonTextKey,
                                               checkItemJsonSubtitleKey = 'subtitle',
                                               checkItemJsonEndSubtitleKey = 'endSubtitle',
                                               checkItemJsonTagKey = 'tag',
                                               checkItemJsonSubtitleTooltipKey = 'subtitleTooltip',
                                               checkItemJsonEndSubtitleTooltipKey = 'endSubtitleTooltip',
                                               checkItemJsonTagTooltipKey = 'tagTooltip',
                                               checkItemJsonIMsgKey = 'iMsg',
                                               checkItemJsonCategoryKey = 'Category',
                                               checkItemJsonCategoryIdKey = 'CategoryId',
                                               checkItemLabelClassName = '',
                                               checkItemSubtitleClassName = '',
                                               checkItemEndSubtitleClassName = '',
                                               checkItemTagClassName = '',
                                               categoryLabelEndComponent,
                                               checkItemEndComponent,
                                               showCheckItemControl1,
                                               showCheckItemControl2,
                                               checkItemControl1Props,
                                               checkItemControl2Props,
                                               msg,

                                               selectPlaceholder = 'Choose an option',
                                               selectDefOption,
                                               selectOptions = [],
                                               selectLabel,
                                               selectId,
                                               selectTextKey = 'text',
                                               selectIdKey = 'id',
                                               onSelectChange,
                                               showDividerBeforeCbCategory = true,
                                               showSelectLoader = false,
                                               selectRequired = false,
                                               searchWithSubtitle = false,
                                               searchWithEndSubtitle = false,
                                               searchWithTag = false,

                                               showSelectAll = true,
                                               showDisplaySelectedOnly = false,
                                               searchable = true,
                                               showSelectedCount = true,
                                               showSearchCount = true,
                                               showTotalCount = true,
                                               showSelect = true,
                                               disableSelect = false,
                                               // checkBinarySelection = false,
                                               onItemCheck,
                                               onCheckAll,
                                               onValueChange,
                                               showDataListLoader = false,
                                               showCheckItemTooltip = false,
                                               showCheckItemTooltipLimit = 40,
                                               scrollLargeCheckItem = true,
                                               showDataList = true,
                                               checkListColumnCount = 1,
                                               totalCountComponent,

                                               onSearch,

                                               style = {},
                                               searchInputStyle = {},
                                               cbStyle = {},
                                               labelStyle = {},
                                               countLabelStyle = {},
                                               headerStyle = {},
                                               msgStyle = {},
                                               sortStyle = {},

                                               selectBelowComponent,
                                               resetDataNow = false,

                                               //sort
                                               showSortBy = false,
                                               sortByParameters = [],//by: id, name, subtitle, subtitle

                                               //search-filters
                                               showFilterBy = false,
                                               filterByParameters = [],//by: id, name, subtitle, subtitle

                                               showTransferredList = false,
                                               transferredListTitle = 'Filtered List',
                                               transferredListSubtitle = 'All the selected items are displayed here',
                                           }) => {
    const TAG = "SearchableCheckboxList";

    const Fields = {
        showSelectLoader: 'showSelectLoader',
        selectId: 'selectId',
        selectText: 'selectText',
        totalCount: 'totalCount',
        selectedCount: 'selectedCount',
        msgText: 'msgText',
        searchValue: 'searchValue',
        allSelected: 'allSelected',
        itemList: 'itemList',
        searchedList: 'searchedList',
        displaySelectedListOnly: 'displaySelectedListOnly',
        selectedList: 'selectedList',

        isSortListOrder_Asc: 'isSortListOrder_Asc',

        dataReseted: 'dataReseted',
    };

    const [searchListPending, startSearchListTransition] = useTransition();
    const [cbSelectPending, startCbSelectTransition] = useTransition();
    const [selectBoxPending, startSelectBoxTransition] = useTransition();
    const [commonTransitionPending, startCommonTransition] = useTransition();

    const [fieldsData, setFieldsData] = useState({});
    const [selectedCbIds, setSelectedCbIds] = useState(new Set());

    // const [showLoading_example, setShowLoading_example] = useState({});
    // const [listSelectPendingx, startListSelectTransitionx] = useTransition_Spring(showLoading_example, null, {
    //     from: {opacity: 0},
    //     enter: {opacity: 1},
    //     leave: {opacity: 0},
    // });
    // const handleCheckboxChange = (e) => {
    //     // Perform your checkbox logic here
    //
    //     // Simulate async process
    //     simulateAsyncProcess();
    // };
    // // Simulate a pending process
    // const simulateAsyncProcess = () => {
    //     setShowLoading_example(true);
    //     createTimeout(() => {
    //         setShowLoading_example(false);
    //     }, 2000); // Replace with your actual async process
    // };

    useEffect(() => {

    }, []);
    useEffect(() => {
        // log('useEffect.checkDataList:', checkDataList, 'len:', getArrLen(checkDataList));
        // log('isCheckItemJson:', isCheckItemJson, 'checkItemJsonIdKey:', checkItemJsonIdKey, 'checkItemJsonTextKey:', checkItemJsonTextKey);
        // if (isArr(checkDataList) && (!isBoolTrue(preventRerender) || (isBoolTrue(preventRerender) && !checkNullArr(getField(Fields.itemList))))) {
        if (checkNullArr(checkDataList) && (!isBoolTrue(preventRerender) || (isBoolTrue(preventRerender) && !checkNullArr(getField(Fields.itemList))))) {
            // if (isArr(checkDataList)) {
            // updateField(Fields.itemList, []);
            // updateField(Fields.searchedList, []);
            if (isArr(checkDataList)) {
                if (isBoolTrue(isCheckItemJson)) {
                    if (!checkNullStr(checkItemJsonIdKey)) throw new Error(`Check-data list items have been specified as objects/json, so it's id-key cannot be null`);
                    if (!checkNullStr(checkItemJsonTextKey)) throw new Error(`Check-data list items have been specified as objects/json, so it's text/name-key cannot be null!`);
                }
            }
            const selectedIds = new Set([]);

            const list = checkDataList.map((item, index) => {
                if (isJsonValueTrue(item, 'selected')) {
                    // if (isJsonValueTrue(item, 'selected') || isJsonValueTrue(item, 'original')) {
                    selectedIds.add(isBoolTrue(isCheckItemJson) ? getJsonValue(item, checkItemJsonIdKey, index) : index);
                }
                return {
                    // id: item, name: item, searched: false, selected: false,
                    ...isBoolTrue(isCheckItemJson) ? item : {},
                    id: isBoolTrue(isCheckItemJson) ? getJsonValue(item, checkItemJsonIdKey, index) : index,
                    name: isBoolTrue(isCheckItemJson) ? getJsonValue(item, checkItemJsonTextKey) : jsonToStr(item),
                    [checkItemJsonIdKey]: isBoolTrue(isCheckItemJson) ? getJsonValue(item, checkItemJsonIdKey, index) : index,
                    [checkItemJsonTextKey]: isBoolTrue(isCheckItemJson) ? getJsonValue(item, checkItemJsonTextKey) : jsonToStr(item),
                    [checkItemJsonSubtitleKey]: isBoolTrue(isCheckItemJson) ? getJsonValue(item, checkItemJsonSubtitleKey) : '',
                    [checkItemJsonEndSubtitleKey]: isBoolTrue(isCheckItemJson) ? getJsonValue(item, checkItemJsonEndSubtitleKey) : '',
                    [checkItemJsonTagKey]: isBoolTrue(isCheckItemJson) ? getJsonValue(item, checkItemJsonTagKey) : '',
                    [checkItemJsonTagTooltipKey]: isBoolTrue(isCheckItemJson) ? getJsonValue(item, checkItemJsonTagTooltipKey) : '',
                    [checkItemJsonIMsgKey]: isBoolTrue(isCheckItemJson) ? getJsonValue(item, checkItemJsonIMsgKey) : '',
                    [checkItemJsonSubtitleTooltipKey]: isBoolTrue(isCheckItemJson) ? getJsonValue(item, checkItemJsonSubtitleTooltipKey) : '',
                    [checkItemJsonEndSubtitleTooltipKey]: isBoolTrue(isCheckItemJson) ? getJsonValue(item, checkItemJsonEndSubtitleTooltipKey) : '',
                    variant: isBoolTrue(isCheckItemJson) ? getJsonValue(item, 'variant') : variant,
                    disabled: isBoolTrue(isCheckItemJson) ? isJsonValueTrue(item, 'disabled') : false,
                    selected: isBoolTrue(isCheckItemJson) ? isJsonValueTrue(item, 'selected') : false,
                    original: isBoolTrue(isCheckItemJson) ? (isJsonValueTrue(item, 'original') ? isJsonValueTrue(item, 'original') : isJsonValueTrue(item, 'selected')) : false,
                    // original: isBoolTrue(isCheckItemJson) ? isJsonValueTrue(item, 'original') : false,//org
                    searched: false,
                }
            });
            updateField(Fields.itemList, checkNullArr(list) ? list : []);
            setSelectedCbIds(selectedIds);
            // log('useEffect.selectedIds:', selectedIds);
            // log('useEffect.checkDataList triggered');
        } else {
            // log('useEffect.checkDataList-empty');
            updateField(Fields.itemList, []);
            updateField(Fields.searchedList, []);
            setSelectedCbIds(new Set());
        }
        // }, [checkDataList, isCheckItemJson, checkItemJsonIdKey, checkItemJsonTextKey,checkDataListSelectedModal]);
    }, [checkDataList, isCheckItemJson, checkItemJsonIdKey, checkItemJsonTextKey, checkItemJsonSubtitleKey, checkItemJsonEndSubtitleKey, checkItemJsonTagKey, checkItemJsonTagTooltipKey, checkItemJsonSubtitleTooltipKey, checkItemJsonEndSubtitleTooltipKey, preventRerender]);

    useEffect(() => {
        // if (isBoolTrue(resetDataNow) && !isFieldTrue(Fields.dataReseted)) {
        // log('useEffect.resetDataNow:', resetDataNow);
        if (isBoolTrue(resetDataNow)) {
            // updateField(Fields.dataReseted, true);
            updateField(Fields.itemList, []);
            updateField(Fields.searchedList, []);
            // setSelectedCbIds(new Set());
            // setFieldsData({});
        } else {
            // updateField(Fields.dataReseted, false);
        }
    }, [resetDataNow]);


    useEffect(() => {
        //search operation
        if (checkNullArr(getField(Fields.itemList, []))) {
            const searchText = (getField(Fields.searchValue) + '').toLowerCase();
            startSearchListTransition(() => {
                updateField(Fields.searchedList, getField(Fields.itemList, []).map((item) => {
                    const name = (item.name + '').toLowerCase();
                    const text = (item[checkItemJsonTextKey] + '').toLowerCase();
                    const subtitle = (item[checkItemJsonSubtitleKey] + '').toLowerCase();
                    const endSubtitle = (item[checkItemJsonEndSubtitleKey] + '').toLowerCase();
                    const tag = (item[checkItemJsonTagKey] + '').toLowerCase();

                    const nameIncluded = name.includes(searchText);
                    const textIncluded = text.includes(searchText);
                    const subtitleIncluded = isBoolTrue(searchWithSubtitle) && checkNullStr(subtitle) && subtitle.includes(searchText);
                    const endSubtitleIncluded = isBoolTrue(searchWithEndSubtitle) && checkNullStr(endSubtitle) && endSubtitle.includes(searchText);
                    const tagIncluded = isBoolTrue(searchWithTag) && checkNullStr(tag) && tag.includes(searchText);

                    const foundItem = textIncluded || nameIncluded || subtitleIncluded || endSubtitleIncluded || tagIncluded;
                    return {
                        ...item,
                        // searched: name.includes(searchText.toLowerCase()),
                        searched: isBoolTrue(showDisplaySelectedOnly)
                            ? !isBoolTrue(getJsonValue(item, 'hide', false)) && foundItem
                            : foundItem
                    }
                }));
            });
            // log('useEffect.itemList | searchValue triggered');
        }
    }, [getField(Fields.itemList, []), getField(Fields.searchValue)]);

    return (<>{createPanel()}</>)

    //-----------------------------UI Functions start-----------------------------------
    function createPanel() {
        return <div className={`m-0 p-0 w-100 ${wrapperClassName}`}>
            {/*header*/}
            {isBoolTrue(showHeader) && <div className={`m-0 p-0 w-100 ${headerClassName}`}>
                {checkNullStr(label) && <Label className={`p-1 px-${isBoolTrue(padded) ? pad : 0} m-0 ${labelClassName}
                ${!(labelClassName + '').includes('fs-') ? '' : ''}`}
                                               textAt={'start'} style={labelStyle} required={required}>
                    {label}
                </Label>}
            </div>}

            {/*content*/}
            <Section
                className={`m-0 p-0 px-0 w-100 py-${!checkIfCheckListFound() ? parseInteger(pad) - 1 : (isBoolTrue(padded) ? parseInteger(pad) - 1 : '0')} ${borderBoxClassName}`}
                bg={'t'}
                borderless={(!isBoolTrue(showDataList)) || borderless}
                borderColor={borderColor}
                overflow={'visible'}>
                {isBoolTrue(showSelect) && createSelect()}
                {selectBelowComponent && selectBelowComponent}

                {isBoolTrue(showDataList) && <div className={'w-100 m-0 m-0'}>

                    {/*select-all & cb-list*/}
                    {(!isBoolTrue(showDataListLoader)) &&
                        <div
                            className={`w-100 m-0 p-0 px-${isBoolTrue(padded) ? (isBoolTrue(inlineHeader) ? 0 : pad) : 0} mt-${!checkIfCheckListFound() ? '0' : '0'}`}>
                            <Section
                                className={`p-0 px-3x m-0 w-100 pt-0 ${scrollWrapperClassName}`}
                                borderless={borderlessList} roundedVariant={roundedVariant}
                                contentClassName={'position-relative'}
                                borderColor={borderColor}
                                bg={'t'}
                                overflow={'visible'}>
                                <div className={`w-100 m-0 p-0`}>
                                    {/*search-box & count-bar*/}
                                    {!isBoolTrue(inlineHeader) && createSearchBoxAndCountBar()}

                                    {/*select-all & show-selected-only cbs*/}
                                    {!isBoolTrue(inlineHeader) && createHeaderToolbar()}

                                    {isBoolTrue(inlineHeader) &&
                                        <div
                                            className={`w-100 m-0 p-0 px-${isBoolTrue(padded) ? pad : 0} row al-center jc-center border-bottom border-bottom-1 border-gray`}>
                                            <div className={`m-0 p-1 col-sm-12 col-md-6`}>
                                                {createSearchBoxAndCountBar(0)}
                                            </div>
                                            <div className={`m-0 p-1 col-sm-12 col-md-6`}>
                                                {createHeaderToolbar()}
                                            </div>
                                        </div>}

                                    {/*cb-list-scroller*/}
                                    {createCbListScrollView()}
                                </div>
                            </Section>
                        </div>}

                    {isBoolTrue(showDataListLoader) &&
                        <div className={`w-100 m-0 p-0 px-${isBoolTrue(padded) ? pad : '0'} mt-2`}>
                            <Section className={'w-100 p-0 m-0'} roundedVariant={roundedVariant}
                                     style={{minHeight: listHeight,}}
                                     bg={'t'}
                                     overflow={'visible'}
                                     borderless={borderlessList}
                                     borderColor={borderColor}>
                                <CircleLoading
                                    variant={variant}
                                    wrapperClassName={'m-0 p-2 mt-0 h-100'}
                                    wrapperHeight={listHeight}/>
                            </Section>
                        </div>
                    }
                </div>}
            </Section>
        </div>
    }

    function createSelect() {
        return <div className={`w-100 m-0 p-0 px-${isBoolTrue(padded) ? pad : '0'}`}>
            <Select
                id={getSelectId()}
                wrapperClassName={`m-0 p-0 pb-${selectBelowPad} ${selectClassName}`}
                innerWrapperClassName={'w-100 m-0 p-0'}
                style={{minWidth: '90%'}}
                variant={variant}
                className={`w-100 py-2 mb-0 m-0 rounded-${roundedVariant}`}
                wrap={`wrap`}
                justifyAt={'start'}
                labelClassName={`px-1 pb-0 text-input-label text-start w-100 ${selectLabelClassName}`}
                required={selectRequired}
                label={selectLabel}
                textKey={selectTextKey}
                idKey={selectIdKey}
                placeholder={selectPlaceholder}
                disabled={disableSelect}
                options={selectOptions}
                defOption={selectDefOption}
                // defOption={getDefStr(getField(Fields.selectId),selectDefOption)}
                showLoader={selectBoxPending || showSelectLoader}
                onChange={(e, text, value) => {
                    // log('Select::text:', text, "value:", value);
                    startSelectBoxTransition(() => {
                        updateField(Fields.selectId, value);
                        updateField(Fields.selectText, text);
                        if (onSelectChange) onSelectChange(e, value, text);
                    });
                }}
            />
        </div>
    }

    function createSearchBoxAndCountBar(padValue = pad) {
        return (isBoolTrue(searchable) || canShowCountBar()) &&
            <Flexbox
                className={`w-100 jc-start-sm flex-nowrap flex-wrap-sm pt-${isBoolTrue(padded) ? padValue : '0'} mb-2x 
                            px-${isBoolTrue(padded) ? padValue : '0'} 
                            pt-${isBoolTrue(padded) ? parseInteger(padValue) - 2 : '0'}`}
                justifyAt={canShowCountBar() ? 'between' : 'center jc-start-sm jc-start-sm'}
                alignAt={'center al-start-sm'}
                position={'relativex'}
                wrap={''}>
                {/*input*/}
                {isBoolTrue(searchable) &&
                    <div className={`w-${(canShowCountBar() && checkIfCheckListFound()) ? '100' : '100'} m-0 p-0 position-relative`}>
                        <Input
                            wrapperClassName={`w-100 pt-2x px-0`}
                            className={`perms-mgmt-mlist-form-control pe-3x ps-5x py-1 rounded-${roundedVariant} ${searchClassName}`}
                            type={'text'}
                            placeholder={searchPlaceholder}
                            disabled={!checkIfCheckListFound()}
                            floatingLabel={false}
                            iconClassName={'fs-sm'}
                            // iconStyle={{marginLeft: '-.35rem'}}
                            iconStyle={{marginRight: '-.5rem'}}
                            pe={3}
                            ps={2}
                            // leftIcon={'fa fa-search'}
                            leftIcon={'fa fa-search ms-n2'}
                            placeholderAsLabel={false}
                            showMsgOverride={false}
                            showLabel={false}
                            style={searchInputStyle}
                            // showMsg={false}
                            value={getField(Fields.searchValue)}
                            rightComponent={<Flexbox justifyAt={'center'} alignAt={'center'}
                                                     position={'absolute'} bg={'infox'}
                                                     top={'.33rem'} right={'.5rem'}
                            >
                                {searchListPending && <div className={'p-0 px-1'} style={{marginLeft: ''}}>{createLoader()}</div>}

                                {(isBoolTrue(showSearchCount) && checkNullStr(getField(Fields.searchValue))) &&
                                    <Label
                                        className={`fs-sm fw-semi-bold select-none position-absolutex p-0 m-0 rounded-10 px-2 d-flex flex-nowrap al-center jc-center`}
                                        style={{top: '-.75rem', right: '2rem'}}
                                        bgColor={'var(--primary10)'}
                                        color={'var(--textColor)'}
                                        height={'24px'}
                                        tooltip={'Search Results'}>
                                        {getArrLen(getSearchedCheckList(true).filter(item => isJsonValueTrue(item, 'searched')))}
                                    </Label>}
                                {/*{isBoolTrue(showFilterBy) && <div className={'p-0 m-0 ms-1'}>{createFilterByDropdown()}</div>}*/}
                            </Flexbox>}
                            onChange={(e) => {
                                // startSearchTypeTransition(() => {
                                updateField(Fields.searchValue, e.target.value);
                                if (onSearch) onSearch(e.target.value);
                                // });
                            }}
                        />
                        {/*{(searchListPending || searchTypePending)*/}
                        {/*{(searchListPending || !searchListPending) && <div className={'p-0 m-0 ps-2 w-10x position-absolute'}*/}
                        {/*                                                   style={{top: '22%', right: '30%'}}>*/}
                        {/*    {createLoader()}*/}
                        {/*</div>}*/}
                    </div>}

                {/*count-bar*/}
                {((checkIfCheckListFound()) || canShowCountBar()) &&
                    <div className={`position-absolutex w-24 p-0 m-0 mb-5x`}
                         style={{top: '-.5rem', right: '.4rem'}}>
                        <div className={'p-0 px-2 m-0 w-100 d-flex fd-row flex-nowrap jc-end al-center'}>
                            {/*count*/}
                            <Label className={'m-0 p-1 fs-sm text-dark text-nowrap'} textAt={'start'}>
                                {/*total-count*/}
                                {(isBoolTrue(showTotalCount) || isBoolTrue(showSelectedCount)) &&
                                checkNull(totalCountComponent)
                                    ? totalCountComponent
                                    : <span className={'px-1 fw-semi-bold'}>
                                        {isBoolTrue(showTotalCount) && <span className={''}>
                                             {/*<span className={'fw-semi-bold'}>Total: </span>*/}
                                            <span>{getArrLen(getCheckList(true).filter(item => isJsonValueTrue(item, 'selected')), 0)}</span>
                                        </span>}
                                        {isBoolTrue(showSelectedCount) && <span className={''}>
                                             {/*<span className={'fw-semi-bold'}>Total: </span>*/}
                                            <span>/{getArrLen(getCheckList(true), 0)}</span>
                                        </span>}
                                </span>}

                                {/*{(isBoolTrue(showTotalCount)&&isBoolTrue(showTotalCount))&&<span>|</span>}*/}

                                {/*selected-count*/}
                                {/*{isBoolTrue(showSelectedCount) &&*/}
                                {/*    <span className={'px-1'}>*/}
                                {/*        <span className={'fw-semi-bold'}>Selected: </span>*/}
                                {/*        {getArrLen(getCheckList(true).filter(item => isBoolTrue(item.selected))) + ' '}*/}
                                {/*    </span>}*/}

                                {/*<span>|</span>*/}

                                {/*selected-count*/}
                                {/*{isBoolTrue(showSearchCount) &&*/}
                                {/*    <span className={'px-1'}>*/}
                                {/*        <span className={'fw-semi-bold'}>Searched: </span>*/}
                                {/*        {(checkNullStr(getField(Fields.searchValue)) && checkNullArr(getSearchedCheckList(true))) ? getArrLen(getSearchedCheckList(true).filter(item => isBoolTrue(item.searched))) : 0}*/}
                                {/*    </span>}*/}
                            </Label>
                        </div>
                    </div>}
            </Flexbox>
    }

    function createHeaderToolbar() {
        return (!isBoolTrue(showDataListLoader) && ((isBoolTrue(showSelectAll) || isBoolTrue(showDisplaySelectedOnly))))
            && <Flexbox
                className={`w-100 m-0 p-0 
                ps-${isBoolTrue(padded) ? pad : pad} pe-4 mb-1 
                ${isBoolTrue(canShowCountBar()) ? 'mt-1' : 'mt-1'}`}
                justifyAt={'start'}
                wrap={'nowrap'}>

                {/*select-all*/}
                <Checkbox
                    wrapperClassName={`mx-1 w-${isBoolTrue(showDisplaySelectedOnly) ? '50' : '100'}`}
                    className={``}
                    labelClassName={`d-flex fd-row flex-nowrap jc-between al-center w-100 position-relative`}
                    highlightHoverVariant={'dark'}
                    tooltip={false}
                    // defaultChecked={selectedCbIds.size == getArrLen(getCheckList().filter(item => isJsonValueTrue(item, 'selected')))}
                    // checked={selectedCbIds.size == getArrLen(getCheckList().filter(item => isJsonValueTrue(item, 'selected')))}
                    defaultChecked={getCheckList().every(item => isJsonValueTrue(item, 'selected'))}
                    checked={getCheckList().every(item => isJsonValueTrue(item, 'selected'))}
                    // checked={!checkIfCheckListFound() || (getCheckList().every(item => isJsonValueTrue(item, 'selected')) && isFieldTrue(Fields.allSelected))}
                    disabled={checkIfNoSearchResultsFound() || !checkIfCheckListFound() || isFieldTrue(Fields.displaySelectedListOnly)}
                    onChange={(e, value) => {
                        const updatedIds = new Set(selectedCbIds);
                        const updatedList = getCheckList().map((item) => {
                            return {
                                ...item,
                                selected: !isFieldTrue(Fields.allSelected),
                            }
                        });
                        updatedList.map((item) => {
                            if (value) {
                                updatedIds.add(item.id);
                            } else {
                                updatedIds.delete(item.id);
                            }
                        })
                        // log('cb.Select-All.value:', value, 'updatedIds:', updatedIds);
                        updateField(Fields.allSelected, !isFieldTrue(Fields.allSelected));
                        updateField(Fields.itemList, updatedList);

                        const selectedList = [];
                        startCbSelectTransition(() => {
                            updatedList.map((item) => {
                                if (isJsonValueTrue(item, 'selected')) {
                                    if (isBoolTrue(isCheckItemJson)) {
                                        if (isBoolTrue(minifySelectedList)) {
                                            selectedList.push({
                                                [checkItemJsonIdKey]: getJsonValue(item, checkItemJsonIdKey),
                                                [checkItemJsonTextKey]: getJsonValue(item, checkItemJsonTextKey),
                                            });
                                        } else {
                                            selectedList.push({
                                                ...item,
                                                selected: true,
                                            });
                                        }
                                    } else {
                                        selectedList.push(getJsonValue(item, checkItemJsonTextKey));
                                    }
                                }
                            });
                            setSelectedCbIds(updatedIds);
                        });
                        if (onCheckAll) onCheckAll(e, value, selectedList, getNewSelectedCheckListItems(updatedList), updatedList);
                        if (onValueChange) onValueChange(e, value, selectedList, getNewSelectedCheckListItems(updatedList), updatedList);
                    }}>
                    <span className={`text-start `}>Select All</span>
                    {(cbSelectPending) && <div
                        className={'p-0 m-0 ps-2 w-10x position-absolute'} style={{top: '0px', right: '2px'}}>
                        {createLoader()}
                    </div>}
                </Checkbox>

                {/*display-selected-only*/}
                <Checkbox
                    wrapperClassName={`mx-1 w-${isBoolTrue(showDisplaySelectedOnly) ? '50' : '100'}`}
                    className={``}
                    labelClassName={`d-flex fd-row flex-nowrap jc-between al-center w-100 position-relative`}
                    highlightHoverVariant={'dark'}
                    tooltip={false}
                    defaultChecked={false}
                    hidden={!isBoolTrue(showDisplaySelectedOnly)}
                    checked={!checkIfCheckListFound() || (isFieldTrue(Fields.displaySelectedListOnly))}
                    disabled={checkIfNoSearchResultsFound() || !checkIfCheckListFound() || isFieldTrue(Fields.allSelected) || (getCheckList().every(item => isJsonValueTrue(item, 'selected')))}
                    onChange={(e, value) => {
                        // log('displaySelectedListOnly.value:', value);
                        startCommonTransition(() => {
                            const updatedList = getCheckList().map((item) => ({
                                ...item,
                                hide: value ? !isBoolTrue(getJsonValue(item, 'selected')) : false,
                            }));
                            updateField(Fields.displaySelectedListOnly, !isFieldTrue(Fields.displaySelectedListOnly));
                            updateField(Fields.itemList, updatedList);
                        });
                    }}>
                    <span className={`text-start `}>Show Selected</span>
                </Checkbox>

                {/*common-loader*/}
                {(commonTransitionPending) && <div
                    className={'p-0 m-0 ps-2 w-10x position-absolute'} style={{top: '0px', right: '2px'}}>
                    {createLoader()}
                </div>}

                {/*sortBy btn*/}
                {isBoolTrue(showSortBy) && createSortByFilterDropdown()}
            </Flexbox>
    }

    function createCbListScrollView() {
        return (!isBoolTrue(showDataListLoader)) &&
            <ScrollView
                className={`px-0 p-0 px-${pad} pb-${parseInteger(pad) - 1} ${isBoolTrue(inlineHeader) ? 'mt-1' : ''} ${scrollerClassName} ${TAG} scroller`}
                // height={'auto'}
                height={listHeight}
                id={scrollerId}
                style={{
                    // height: 'auto',
                    // maxHeight: listHeight
                }}
                scrollVariant={scrollVariant}>
                <div className={`w-100 m-0 p-0 row ${listWrapperClassName}`}>
                    {(checkIfSearchedListFound()) ? getSearchedCheckList().map((cbItem, cbIndex) => (isJsonValueTrue(cbItem, 'searched') &&
                            ((isJsonValueTrue(cbItem, checkItemJsonCategoryKey))
                                //category-label
                                ? <div className={`w-100 m-0 p-0 px-1 ${cbCategoryWrapperClassName}`}>
                                    {/*ctg-divider*/}
                                    {isBoolTrue(showDividerBeforeCbCategory) && <HorDiv variant={'gray'}/>}

                                    {/*ctg-label*/}
                                    {checkNullStr(getJsonValue(cbItem, checkItemJsonTextKey)) &&
                                        <Label className={`m-0 p-0 py-1 px-3 text-input-label w-100 ${cbCategoryLabelClassName}`}
                                               textAt={cbCategoryLabelAt}
                                               id={getJsonValue(cbItem, checkItemJsonCategoryIdKey)}>
                                            {getJsonValue(cbItem, checkItemJsonTextKey)}
                                            {getJsonValue(cbItem, 'categoryLabelEndComponent', categoryLabelEndComponent)}
                                        </Label>}
                                </div>
                                : <div key={cbIndex + 'xx' + getJsonValue(cbItem, checkItemJsonIdKey)}
                                       className={`m-0 p-0 pb-1 col-sm-12 col-md-${checkListCheckItemGridWidthValue()} ${parseInteger(checkListColumnCount) === 1 ? 'px-0' : 'pe-1'}`}>
                                    {/*cb*/}
                                    <span className={'w-100 d-flex al-center jc-start fd-row flex-nowrap  position-relative'}>
                                        <Checkbox
                                            wrapperClassName={`w-100x w-autox ${(isBoolTrue(originalCheckItemOutlined) && isJsonValueTrue(cbItem, 'original')) ? `` : 'border-nonex'} ${cbWrapperClassName}`}
                                            className={`${cbClassName}`}
                                            tooltip={showCheckItemTooltip}
                                            tooltipWordLimit={showCheckItemTooltipLimit}
                                            scrolled={scrollLargeCheckItem}
                                            disabled={isJsonValueTrue(cbItem, 'disabled')}
                                            hidden={isBoolTrue(getJsonValue(cbItem, 'hide', false))}
                                            tooltipText={getJsonValue(cbItem, checkItemJsonTextKey)}
                                            subtitle={getJsonValue(cbItem, checkItemJsonSubtitleKey)}
                                            endSubtitle={getJsonValue(cbItem, checkItemJsonEndSubtitleKey)}
                                            tag={getJsonValue(cbItem, checkItemJsonTagKey)}
                                            subtitleTooltip={getJsonValue(cbItem, checkItemJsonSubtitleTooltipKey)}
                                            endSubtitleTooltip={getJsonValue(cbItem, checkItemJsonEndSubtitleTooltipKey)}
                                            tagTooltip={getJsonValue(cbItem, checkItemJsonTagTooltipKey)}
                                            iMsg={getJsonValue(cbItem, checkItemJsonIMsgKey)}
                                            highlightHoverVariant={'dark'}
                                            selectedHighlightVariant={getJsonValue(cbItem, 'variant', variant)}
                                            variant={getJsonValue(cbItem, 'variant')}
                                            labelClassName={checkItemLabelClassName}
                                            subtitleClassName={checkItemSubtitleClassName}
                                            endSubtitleClassName={checkItemEndSubtitleClassName}
                                            tagClassName={checkItemTagClassName}
                                            // defaultChecked={isJsonValueTrue(cbItem, 'selected')}
                                            // checked={isJsonValueTrue(cbItem, 'selected')}
                                            defaultChecked={selectedCbIds.has(cbItem.id)}
                                            checked={selectedCbIds.has(cbItem.id)}
                                            // defaultChecked={[...selectedCbIds].includes(cbItem.id)}
                                            // checked={[...selectedCbIds].includes(cbItem.id)}
                                            outlined={isBoolTrue(originalCheckItemOutlined) && isJsonValueTrue(cbItem, 'original')}
                                            wrapperStyle={{
                                                // border: (isBoolTrue(originalCheckItemOutlined) && isJsonValueTrue(cbItem, 'original')) ? `1.3px solid var(--${variant}Dark)` : 'none'
                                            }}
                                            endComponent={(isBoolTrue(showCheckItemControl1) || isBoolTrue(showCheckItemControl2) || checkNull(getJsonValue(cbItem, 'endComponent', checkItemEndComponent))) &&
                                                <span
                                                    className={'d-flex flex-row flex-nowrap px-1x w-auto al-center jc-end position-absolute'}
                                                    style={{top: '-.1rem', right: '.05rem', zIndex: 5}}>
                                                  {isBoolTrue(showCheckItemControl1) &&
                                                      <Button
                                                          {...checkNullJson(checkItemControl1Props) ? checkItemControl1Props : {}}
                                                          onMouseEnter={(e) => {
                                                              e.preventDefault();
                                                          }}
                                                          onClick={(e) => {
                                                              e.preventDefault();
                                                              const onClick = getJsonValue(checkItemControl1Props, 'onClick');
                                                              if (checkNull(onClick)) {
                                                                  onClick(cbItem, cbIndex);
                                                              }
                                                          }}
                                                      />}
                                                    {isBoolTrue(showCheckItemControl2) &&
                                                        <Button
                                                            {...checkNullJson(checkItemControl2Props) ? checkItemControl2Props : {}}
                                                            onMouseEnter={(e) => {
                                                                e.preventDefault();
                                                            }}
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                const onClick = getJsonValue(checkItemControl2Props, 'onClick');
                                                                if (checkNull(onClick)) {
                                                                    onClick(cbItem, cbIndex);
                                                                }
                                                            }}
                                                        />}

                                        </span>}
                                            onChange={(e, checked) => {
                                                // log('cb.onChange.name:', cbItem[checkItemJsonTextKey], 'id:', cbItem.id, 'selected:', isJsonValueTrue(cbItem, 'selected'), ':', selectedCbIds.has(cbItem.id), 'selectedCbIds:', selectedCbIds);
                                                // const checked=e.target.checked;
                                                // startCbSelectTransition(() => {
                                                const name = getJsonValue(cbItem, checkItemJsonTextKey) + '';
                                                const id = getJsonValue(cbItem, checkItemJsonIdKey) + '';
                                                const updatedIds = new Set(selectedCbIds);
                                                const updatedList = getCheckList().map(item => {
                                                    // log('getCheckList().item-id:', getJsonValue(item, checkItemJsonIdKey), 'id:', id, 'checked:', checked, 'cb-id===getCheckList().item.id:', getJsonValue(item, checkItemJsonIdKey) === id);
                                                    if (isBoolTrue(isCheckItemJson)) {
                                                        if (getJsonValue(item, checkItemJsonIdKey) + '' === id) {
                                                            // log('getCheckList().item-id:', getJsonValue(item, checkItemJsonIdKey), 'id:', id, 'checked:', checked, 'cb-id===getCheckList().item.id:', getJsonValue(item, checkItemJsonIdKey) === id);
                                                            return {...item, selected: !isJsonValueTrue(item, 'selected')};
                                                        }
                                                        return item;
                                                    } else if (getJsonValue(item, checkItemJsonTextKey) + '' === name) {
                                                        // return {...item, selected: !isJsonValueTrue(item, 'selected')};
                                                    }
                                                    return item;
                                                });
                                                startCbSelectTransition(() => {
                                                    // log('updatedList:', sliceArr(updatedList, 0, 5), 'len:', getArrLen(updatedList));
                                                    if (!checked) {
                                                        updatedIds.delete(cbItem.id);
                                                    } else {
                                                        updatedIds.add(cbItem.id);
                                                    }
                                                    setSelectedCbIds(updatedIds);
                                                    updateField(Fields.itemList, updatedList);
                                                });

                                                const selectedList = [];
                                                updatedList.map((item) => {
                                                    if (isJsonValueTrue(item, 'selected')) {
                                                        if (isBoolTrue(isCheckItemJson)) {
                                                            if (isBoolTrue(minifySelectedList)) {
                                                                selectedList.push({
                                                                    [checkItemJsonIdKey]: getJsonValue(item, checkItemJsonIdKey),
                                                                    [checkItemJsonTextKey]: getJsonValue(item, checkItemJsonTextKey),
                                                                });
                                                            } else {
                                                                selectedList.push({
                                                                    ...item,
                                                                    selected: true,
                                                                });
                                                            }
                                                        } else {
                                                            selectedList.push(getJsonValue(item, checkItemJsonTextKey));
                                                        }
                                                    }
                                                });
                                                if (onItemCheck) onItemCheck(e, checked, selectedList, getNewSelectedCheckListItems(updatedList), updatedList);
                                                if (onValueChange) onValueChange(e, checked, selectedList, getNewSelectedCheckListItems(updatedList), updatedList);
                                                // });
                                            }}>
                                        {/*{isBoolTrue(highlightMatchedItem)*/}
                                            {/*    ? highlightMatchedText(getJsonValue(cbItem, checkItemJsonTextKey))*/}
                                            {/*    : getJsonValue(cbItem, checkItemJsonTextKey)}*/}
                                            {isBoolTrue(highlightMatchedItem)
                                                ? highlightMatchedText(limitStringWords(getJsonValue(cbItem, checkItemJsonTextKey), showCheckItemTooltipLimit, '', '...'))
                                                : limitStringWords(getJsonValue(cbItem, checkItemJsonTextKey), showCheckItemTooltipLimit, '', '...')}
                                        </Checkbox>
                                        {getJsonValue(cbItem, 'endComponent', checkItemEndComponent)}
                                   </span>
                                </div>)))
                        : ''}
                </div>
                {(checkIfNoSearchResultsFound() || !checkIfCheckListFound())
                    && <Label className={`m-0 p-1 text-gray-dark fs-sm ${msgClassName}`} textAt={'start'}>
                        {!checkIfCheckListFound() ? noDataFoundMsg : noSearchFoundMsg}
                    </Label>}
            </ScrollView>
    }

    function createLoader() {
        return <CircleLoading
            variant={variant}
            wrapperClassName={'m-0 p-0 mt-0'}
            borderWidth={'2px'}
            size={'14px'}/>
    }

    function createFilterByDropdown() {
        return <ButtonDropdown
            className={'ps-2'}
            buttonClassName={`highlight-${variant}-hover border-${variant}-hover`}
            dropdownItems={getFilterByParams()}
            text={'Filter By'}
            menuItemNameKey={checkItemJsonTextKey}
            menuItemIdKey={checkItemJsonIdKey}
            dividerKey={'divider'}
            highlightableKey={'highlightable'}
            onMenuItemClick={(name, index, item) => {
                log('showFilterBy.onMenuItemClick.name:', name, 'index:', index);
            }}
        />
    }

    function createSortByFilterDropdown() {
        return <ButtonDropdown
            className={'ps-2'}
            buttonClassName={`highlight-${variant}-hover border-${variant}-hover`}
            dropdownItems={getSortByParams()}
            text={'Sort By'}
            menuItemNameKey={checkItemJsonTextKey}
            menuItemIdKey={checkItemJsonIdKey}
            dividerKey={'divider'}
            highlightableKey={'highlightable'}
            onMenuItemClick={(name, index, item) => {
                log('showSortBy.onMenuItemClick.name:', name, 'index:', index);
            }}
        />
    }

    //-----------------------------UI Functions end-----------------------------------


    function updateField(field, value) {
        // log(`updateField.field: ${field}, value: ${value}`);
        setFieldsData(prevState => ({...prevState, [field]: value}));
    }

    function getField(field, defValue) {
        return getJsonValue(fieldsData, field, defValue);
    }

    function isFieldTrue(field) {
        return isBoolTrue(getField(field));
    }

    function getCheckList(filtered = false) {
        if (checkNullArr(getField(Fields.itemList, []))) {
            if (isBoolTrue(filtered)) {
                return getField(Fields.itemList, []).filter(item => !isJsonValueTrue(item, checkItemJsonCategoryKey));
            } else {
                return getField(Fields.itemList, []);
            }
        } else {
            return [];
        }
    }

    function getSearchedCheckList(filtered = false) {
        if (checkNullArr(getField(Fields.searchedList, []))) {
            if (isBoolTrue(filtered)) {
                return getField(Fields.searchedList, []).filter(item => !isJsonValueTrue(item, checkItemJsonCategoryKey));
            } else {
                return getField(Fields.searchedList, []);
            }
        } else {
            return [];
        }
    }

    function checkIfCheckListFound() {
        return checkNullArr(getCheckList());
    }

    function checkIfSearchedListFound() {
        return checkNullArr(getSearchedCheckList());
    }

    function checkIfNoSearchResultsFound() {
        return (checkIfCheckListFound() && (getCheckList().every(item => !(getJsonValue(item, checkItemJsonTextKey) + '').toLowerCase().includes((getField(Fields.searchValue) + '').toLowerCase()))));
    }

    function canShowCountBar() {
        return isBoolTrue(showSelectedCount) || isBoolTrue(showSearchCount) || isBoolTrue(showTotalCount);
    }

    function getSelectId() {
        return getDefStr(selectId, (TAG + selectIdKey + selectTextKey + checkItemJsonIdKey));
    }

    function checkListCheckItemGridWidthValue() {
        const columnWidths = {
            1: 12,
            2: 6,
            3: 4,
            4: 3,
            6: 2,
        };
        return getDefStr(columnWidths[parseInteger(checkListColumnCount)], 12);
    }

    function getNewSelectedCheckListItems(checkList_Optional = []) {
        const fun = 'getNewSelectedCheckListItems:';
        let searchedList = [];
        try {
            const checkList_Org = checkNullArr(checkList_Optional) ? checkList_Optional : getCheckList();
            // log(fun, 'checkList_Optional:', sliceArr(checkList_Optional, 0, 2), 'len:', getArrLen(checkList_Optional));
            // log(fun, 'checkList_Org:', sliceArr(checkList_Org, 0, 2), 'len:', getArrLen(checkList_Org));
            if (checkNullArr(checkList_Org)) {
                let newCheckList = checkList_Org;
                // log(fun, 'newCheckList:', sliceArr(newCheckList, 0, 2), 'len:', getArrLen(newCheckList));
                if (checkNullArr(newCheckList)) {
                    checkList_Org.map((orgItem) => {
                        newCheckList.map((newItem) => {
                            const orgItemId = getJsonValue(orgItem, checkItemJsonIdKey) + '';
                            const newItemId = getJsonValue(newItem, checkItemJsonIdKey) + '';
                            const isNewItemOriginal = isJsonValueTrue(newItem, 'original');
                            const isNewItemSelected = isJsonValueTrue(newItem, 'selected');
                            if (orgItemId === newItemId) {
                            } else {
                                if (isNewItemSelected && !isNewItemOriginal) {
                                    if (isBoolTrue(minifySelectedList)) {
                                        searchedList.push({
                                            [checkItemJsonIdKey]: getJsonValue(newItem, checkItemJsonIdKey),
                                            [checkItemJsonTextKey]: getJsonValue(newItem, checkItemJsonTextKey),
                                        });
                                    } else {
                                        searchedList.push(newItem);
                                    }
                                }
                            }
                        });
                    });
                    searchedList = dropJsonListDuplicates(searchedList, checkItemJsonIdKey);
                    // log('checkListKey:',checkListKey,'searchedList:', sliceArr(checkList_Org, 0, 2), 'len:', getArrLen(checkList_Org));
                    // log(fun, 'searchedList:', sliceArr(searchedList, 0, 2), 'len:', getArrLen(searchedList));
                    return searchedList;
                }
            }
            return searchedList;
        } catch (e) {
            logErr(fun, e);
            return [];
        }
    }

    function getFilterByParams() {
        return [
            {name: getDefStr(checkItemJsonIdKey, checkItemJsonIdKey)},
            {name: getDefStr(checkItemJsonTextKey, checkItemJsonTextKey)},
            {name: getDefStr(checkItemJsonSubtitleKey, checkItemJsonSubtitleKey)},
            {name: getDefStr(checkItemJsonEndSubtitleKey, checkItemJsonEndSubtitleKey)},
            {name: getDefStr(checkItemJsonTagKey, checkItemJsonTagKey)},
            {name: 'Original'},
            {name: 'Clear Filters', variant: 'danger', divider: true, highlightable: false},
        ]
    }

    function getSortByParams() {
        return [
            {name: getDefStr(checkItemJsonIdKey, checkItemJsonIdKey)},
            {name: getDefStr(checkItemJsonTextKey, checkItemJsonTextKey)},
            {name: getDefStr(checkItemJsonSubtitleKey, checkItemJsonSubtitleKey)},
            {name: getDefStr(checkItemJsonTagKey, checkItemJsonTagKey)},
            {name: getDefStr(checkItemJsonEndSubtitleKey, checkItemJsonEndSubtitleKey)},
            {name: 'Original',},
            {name: 'Clear Sorting', variant: 'danger', divider: true, highlightable: false},
        ]
    }

    function highlightMatchedText(text) {
        if (checkNullStr(getField(Fields.searchValue)) && isBoolTrue(highlightMatchedItem)) {
            const searchTerm = (getField(Fields.searchValue, '') + '').toLowerCase();
            const regex = new RegExp(`(${searchTerm})`, 'gi');
            return (text + '').split(regex).map((part, index) => (
                // regex.test((part + '').toLowerCase().trim()) ? <span key={index} className="bg-warning">{part}</span> : part
                regex.test((part + '').toLowerCase()) ? <span key={index} className={`bg-${highlightMatchedColorVariant}`}>{part}</span> : part
            ));
        } else {
            return text;
        }
    }


    function log(...text) {
        printLog(TAG, ...text);
    }

    function logErr(...text) {
        printError(TAG, ...text);
    }
});
export default SearchableCheckboxList;
